'use client'

import Image from 'next/image'
import { useMemo, useState } from 'react'
import { useTheme } from 'next-themes'

interface SectionVideoProps {
  videoSrc: string
  videoPoster?: string
  ariaLabel: string
  priority: boolean
  isInView: boolean
}

export default function SectionVideo({
  videoSrc,
  videoPoster,
  ariaLabel,
  priority,
  isInView,
}: SectionVideoProps) {
  const [loadedVideoSrc, setLoadedVideoSrc] = useState<string | null>(null)
  const { resolvedTheme } = useTheme()

  const isMounted = resolvedTheme !== undefined
  const themedVideoSrc = useMemo(() => {
    if (!isMounted) return undefined

    // The homepage ships one video path per theme. Keeping the suffix assembly
    // here avoids passing both variants through the server tree.
    const themeSuffix = resolvedTheme === 'dark' ? '-dark' : '-light'
    return `${videoSrc}${themeSuffix}.mp4?v=3`
  }, [videoSrc, resolvedTheme, isMounted])

  const isVideoLoaded = isMounted && loadedVideoSrc === themedVideoSrc

  return (
    <figure
      className="aspect-video relative rounded-none md:rounded-2xl overflow-hidden md:shadow-2xl"
      aria-label={ariaLabel}
    >
      {videoPoster && (
        <Image
          src={videoPoster}
          alt={ariaLabel}
          fill
          priority={priority}
          loading={priority ? undefined : 'lazy'}
          className={`object-cover transition-opacity duration-500 ${
            isVideoLoaded ? 'opacity-0' : 'opacity-100'
          }`}
          sizes="(max-width: 768px) 100vw, 50vw"
        />
      )}
      {themedVideoSrc && isInView && (
        <video
          key={themedVideoSrc}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${
            isVideoLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          autoPlay
          loop
          muted
          playsInline
          preload={priority ? 'auto' : 'metadata'}
          onLoadedData={() => setLoadedVideoSrc(themedVideoSrc)}
          aria-label={ariaLabel}
        >
          <source src={themedVideoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
    </figure>
  )
}
